import { Summary } from "../components/summary/Summary";
import { Particulars } from "../components/tab-sections/Particulars";
import { NotableItems } from "../components/NotableItems";
import { Grading } from "../components/tab-sections/Grading";
import { Documents } from "../components/tab-sections/Documents";
import { Images } from "../components/images/Images";
import { GradeComparison } from "../components/tab-sections/GradeComparison";
import { VesselInspectionDataResponse } from "../types/responses/VesselInspectionDataResponse";
import { Defects } from "../components/Defects";
import { Decarbonisation } from "../components/tab-sections/Decarbonisation";
import { AdjustedGrading } from "../components/tab-sections/AdjustedGrading";
import { hasAdjustedGradingSupplement } from "../utils/DocumentHelpers";
import { APIGrade } from "@idwal/idwal-react-components/dist/types/APIGrade";
import { canShowDataSharingOptIn } from "../utils/dataSharingHelpers";
import { DataSharingStatus } from "../constants/DataSharingConstants";

const getIdTabs = (
    vesselInspectionDataResponse: VesselInspectionDataResponse,
    documentData: any,
    t: any,
    dataSharingStatusOverride?: DataSharingStatus | null
) => {
    const lockTabs = canShowDataSharingOptIn(
        dataSharingStatusOverride,
        vesselInspectionDataResponse.vesselInspectionResponse
            ?.dataSharingAvailable,
        vesselInspectionDataResponse.vesselInspectionResponse
            ?.InspectionTypeShortName
    );
    const isFullReport =
        !vesselInspectionDataResponse.vesselInspectionResponse?.earlyRelease;

    const tabs = [
        {
            title: t("tabs.summary"),
            content: (
                <Summary
                    documentData={documentData}
                    vesselInspectionResponse={
                        vesselInspectionDataResponse.vesselInspectionResponse
                    }
                    dataSharingStatusOverride={dataSharingStatusOverride}
                />
            ),
            disabled: false
        },
        {
            title: t("tabs.particulars"),
            content: (
                <Particulars
                    data={vesselInspectionDataResponse.vesselInspectionResponse}
                    isLoading={vesselInspectionDataResponse.isLoading}
                    error={vesselInspectionDataResponse.error}
                    isError={vesselInspectionDataResponse.isError}
                />
            ),
            disabled: lockTabs === true
        }
    ];

    if (isFullReport) {
        tabs.push({
            title: t("tabs.grade_comparison"),
            content: (
                <GradeComparison
                    imo={
                        vesselInspectionDataResponse.vesselInspectionResponse
                            ?.IMONumber
                    }
                    vesselGrade={
                        {
                            grade: vesselInspectionDataResponse
                                .vesselInspectionResponse?.Grade
                        } as APIGrade
                    }
                />
            ),
            disabled: lockTabs === true
        });
    }

    tabs.push(
        {
            title: t("tabs.grading"),
            content: <Grading idReport={true} />,
            disabled: lockTabs === true
        },
        {
            title: t("tabs.defects"),
            content: <Defects />,
            disabled: lockTabs === true
        },
        {
            title: t("tabs.decarbonisation"),
            content: <Decarbonisation />,
            disabled: lockTabs === true
        },
        {
            title: t("tabs.documents"),
            content: <Documents linkTabIndex={7} />,
            disabled: lockTabs === true
        },
        {
            title: t("tabs.supplements"),
            content: <Documents supplements />,
            disabled: lockTabs === true
        },
        {
            title: t("tabs.images"),
            content: <Images />,
            disabled: lockTabs === true
        }
    );

    return tabs;
};

const getDefaultTabs = (
    vesselInspectionDataResponse: VesselInspectionDataResponse,
    documentData: any,
    t: any,
    dataSharingStatusOverride?: DataSharingStatus | null
) => {
    const lockTabs = canShowDataSharingOptIn(
        dataSharingStatusOverride,
        vesselInspectionDataResponse.vesselInspectionResponse
            ?.dataSharingAvailable,
        vesselInspectionDataResponse.vesselInspectionResponse
            ?.InspectionTypeShortName
    );
    const isFullReport =
        !vesselInspectionDataResponse.vesselInspectionResponse?.earlyRelease;

    const tabs = [
        {
            title: t("tabs.summary"),
            content: (
                <Summary
                    documentData={documentData}
                    vesselInspectionResponse={
                        vesselInspectionDataResponse.vesselInspectionResponse
                    }
                    dataSharingStatusOverride={dataSharingStatusOverride}
                />
            ),
            disabled: false
        },
        {
            title: t("tabs.particulars"),
            content: (
                <Particulars
                    data={vesselInspectionDataResponse.vesselInspectionResponse}
                    isLoading={vesselInspectionDataResponse.isLoading}
                    error={vesselInspectionDataResponse.error}
                    isError={vesselInspectionDataResponse.isError}
                />
            ),
            disabled: lockTabs === true
        },
        {
            title: t("tabs.notable_items"),
            content: <NotableItems />,
            disabled: lockTabs === true
        },
        {
            title: t("tabs.grading"),
            content: (
                <Grading
                    idReport={false}
                    inspectionType={
                        vesselInspectionDataResponse.vesselInspectionResponse
                            ?.InspectionType
                    }
                />
            ),
            disabled: lockTabs === true
        },
        {
            title: t("tabs.decarbonisation"),
            content: <Decarbonisation />,
            disabled: lockTabs === true
        },
        {
            title: t("tabs.documents"),
            content: <Documents linkTabIndex={6} />,
            disabled: lockTabs === true
        },
        {
            title: t("tabs.supplements"),
            content: <Documents supplements />,
            disabled: lockTabs === true
        },
        {
            title: t("tabs.images"),
            content: <Images />,
            disabled: lockTabs === true
        }
    ];

    if (isFullReport) {
        tabs.push({
            title: t("tabs.grade_comparison"),
            content: (
                <GradeComparison
                    imo={
                        vesselInspectionDataResponse.vesselInspectionResponse
                            ?.IMONumber
                    }
                    vesselGrade={
                        {
                            grade: vesselInspectionDataResponse
                                .vesselInspectionResponse?.Grade
                        } as APIGrade
                    }
                />
            ),
            disabled: lockTabs === true
        });
    }

    return tabs;
};

const getConditionInspectionTabs = (
    vesselInspectionDataResponse: VesselInspectionDataResponse,
    documentData: any,
    t: any,
    dataSharingStatusOverride?: DataSharingStatus | null
) => {
    const lockTabs = canShowDataSharingOptIn(
        dataSharingStatusOverride,
        vesselInspectionDataResponse.vesselInspectionResponse
            ?.dataSharingAvailable,
        vesselInspectionDataResponse.vesselInspectionResponse
            ?.InspectionTypeShortName
    );
    return [
        {
            title: t("tabs.summary"),
            content: (
                <Summary
                    documentData={documentData}
                    vesselInspectionResponse={
                        vesselInspectionDataResponse.vesselInspectionResponse
                    }
                    dataSharingStatusOverride={dataSharingStatusOverride}
                />
            ),
            disabled: false
        },
        {
            title: t("tabs.particulars"),
            content: (
                <Particulars
                    data={vesselInspectionDataResponse.vesselInspectionResponse}
                    isLoading={vesselInspectionDataResponse.isLoading}
                    error={vesselInspectionDataResponse.error}
                    isError={vesselInspectionDataResponse.isError}
                />
            ),
            disabled: lockTabs === true
        },
        {
            title: t("tabs.notable_items"),
            content: <NotableItems />,
            disabled: lockTabs === true
        },
        {
            title: t("tabs.grading"),
            content: <Grading idReport={false} />,
            disabled: lockTabs === true
        },
        {
            title: t("tabs.decarbonisation"),
            content: <Decarbonisation />,
            disabled: lockTabs === true
        },
        {
            title: t("tabs.documents"),
            content: <Documents linkTabIndex={6} />,
            disabled: lockTabs === true
        },
        {
            title: t("tabs.supplements"),
            content: <Documents supplements />,
            disabled: lockTabs === true
        },
        {
            title: t("tabs.images"),
            content: <Images />,
            disabled: lockTabs === true
        }
    ];
};

export const getTabsForInspection = (
    vesselInspectionDataResponse: VesselInspectionDataResponse,
    documentData: any,
    t: any,
    dataSharingStatusOverride?: DataSharingStatus | null
) => {
    const vesselInspectionResponse =
        vesselInspectionDataResponse.vesselInspectionResponse;
    if (vesselInspectionResponse == undefined) {
        return [];
    }

    let tabs = [];

    switch (vesselInspectionResponse.InspectionType) {
        case "ID Condition Inspection":
            tabs = getIdTabs(
                vesselInspectionDataResponse,
                documentData,
                t,
                dataSharingStatusOverride
            );
            break;
        case "Condition Inspection":
            tabs = getConditionInspectionTabs(
                vesselInspectionDataResponse,
                documentData,
                t,
                dataSharingStatusOverride
            );
            break;
        default:
            tabs = getDefaultTabs(
                vesselInspectionDataResponse,
                documentData,
                t,
                dataSharingStatusOverride
            );
            break;
    }

    if (hasAdjustedGradingSupplement(documentData?.documents)) {
        tabs.push({
            title: t("tabs.adjusted_grading"),
            content: <AdjustedGrading />,
            disabled:
                canShowDataSharingOptIn(
                    dataSharingStatusOverride,
                    vesselInspectionResponse?.dataSharingAvailable,
                    vesselInspectionResponse?.InspectionTypeShortName
                ) === true
        });
    }

    return tabs;
};
